import { alwaysRetainsIrpf } from '@/utils/irpfPolicy';
import { formatLocalDate, toLocalDateKey } from '@/utils/localDateUtils';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Contrato, Pago, EstadoContrato, TipoPago, TipoAlquiler, DestinoInmueble } from '@/types';
import contractService from '@/services/contractService';
import paymentService from '@/services/paymentService';
import adjustmentService from '@/services/adjustmentService';
import MainLayout from '@/components/layout/MainLayout';
import DataTable, { Column } from '@/components/common/DataTable';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import { getClientFullName } from '@/utils/clientUtils';
import { getPaymentStatusBadge } from '@/utils/paymentStatus';
import UpdateDiaPagoModal from '@/components/contracts/UpdateDiaPagoModal';
import TransferContractModal from '@/components/contracts/TransferContractModal';
import { useAuth } from '@/contexts/AuthContext';
import { useDialog } from '@/hooks/useDialog';

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showAlert, showConfirm } = useDialog();
  const [contract, setContract] = useState<Contrato | null>(null);
  const [payments, setPayments] = useState<Pago[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalForm, setLegalForm] = useState({ fecha_ingreso: '', notas: '' });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelForm, setCancelForm] = useState({ fecha_cancelacion: '' });
  const [cancellationBalances, setCancellationBalances] = useState<any>(null);
  const [showUpdateDiaPagoModal, setShowUpdateDiaPagoModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [adjustmentHistory, setAdjustmentHistory] = useState<any[]>([]);
  const [loadingAdjustments, setLoadingAdjustments] = useState(false);
  const [expandedFactors, setExpandedFactors] = useState<Set<number>>(new Set());
  const [showNotes, setShowNotes] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (!id) return;
        const c = await contractService.getContractById(Number(id));
        setContract((c as any).data || c);
        const p = await paymentService.getPaymentsByContract(Number(id));
        const paymentsData = (p as any).data || p;
        setPayments(Array.isArray(paymentsData) ? paymentsData : []);

        // Load adjustment history
        loadAdjustmentHistory(Number(id));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const loadAdjustmentHistory = async (contractId: number) => {
    try {
      setLoadingAdjustments(true);
      const res = await adjustmentService.getAdjustmentHistory(contractId);
      const data = (res as any).data || res;
      // Backend returns { history: [...], contrato_id: 9 }
      const history = data.history || data;
      const historyArray = Array.isArray(history) ? history : [];
      setAdjustmentHistory(historyArray);
      // All factors collapsed by default
      setExpandedFactors(new Set());
    } catch (error) {
      console.error('Error loading adjustment history:', error);
      setAdjustmentHistory([]);
    } finally {
      setLoadingAdjustments(false);
    }
  };

  const toggleFactorExpansion = (index: number) => {
    setExpandedFactors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const calculatePreviousAmount = (newAmount: number, factor: number): number => {
    // Calculate what the amount was before adjustment: previous = new / (1 + factor)
    return Math.round((newAmount / (1 + factor)) * 100) / 100;
  };

  const getStatusColor = (estado: string) => {
    const colors: Record<string, string> = {
      [EstadoContrato.ACTIVO]: 'bg-green-100 text-green-800',
      [EstadoContrato.BORRADOR]: 'bg-gray-100 text-gray-800',
      [EstadoContrato.FINALIZADO]: 'bg-blue-100 text-blue-800',
      [EstadoContrato.RESCINDIDO]: 'bg-red-100 text-red-800',
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  const getFormaPagoLabel = (formaPago?: string) => {
    return 'Mes Vencido';
  };

  const getTipoReajusteLabel = (tipoReajuste?: string) => {
    return 'Total';
  };

  const getTipoAlquilerLabel = (tipoAlquiler?: string) => {
    const labels: Record<string, string> = {
      [TipoAlquiler.ASEGURADO]: 'Asegurado',
      [TipoAlquiler.NO_ASEGURADO]: 'No Asegurado',
    };
    return labels[tipoAlquiler || TipoAlquiler.NO_ASEGURADO] || 'No Asegurado';
  };

  const ownerPayment = payments.find(p => p.tipo === 'PROPIETARIO' && p.estado === 'PENDIENTE')
    ?? payments.find(p => p.tipo === 'PROPIETARIO');
  let ownerBreakdown: Record<string, number> = {};
  try { ownerBreakdown = JSON.parse(ownerPayment?.desglose ?? '{}'); } catch { /* Older payments may have no breakdown. */ }
  const currentCalculation = contract?.liquidacion_propietario;
  const tenantCommissionAmount = Number(currentCalculation?.commission ?? ownerBreakdown.comision_administracion ?? 0);
  const tenantCommissionIvaAmount = Number(currentCalculation?.iva_commission ?? ownerBreakdown.iva_comision ?? 0);
  const ownerIrpfAmount = contract?.inquilino_agente_retencion_irpf
    ? Number(currentCalculation?.breakdown.retencion_irpf_inquilino ?? ownerBreakdown.retencion_irpf_inquilino ?? 0)
    : (contract?.propietarios ?? []).reduce((sum, owner) => sum + Number(owner.irpf_mensual ?? 0), 0);
  const isInLegalList = Boolean(contract?.en_lista_juridica);
  const isInPaymentAgreement = Boolean(contract?.en_convenio);

  const paymentColumns: Column<Pago>[] = [
    {
      key: 'tipo',
      label: 'Tipo',
      sortable: true,
      render: (payment) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          payment.tipo === TipoPago.INQUILINO ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
        }`}>
          {payment.tipo}
        </span>
      )
    },
    {
      key: 'nombre',
      label: 'Inquilino / Propietario',
      sortable: false,
      render: (payment: any) => {
        const isInquilino = payment.tipo === TipoPago.INQUILINO;
        const nombre = isInquilino ? payment.inquilino_nombre : payment.propietario_nombre;
        
        if (!nombre) {
          return <span className="text-gray-400 italic">Sin asignar</span>;
        }
        
        return (
          <>
            <span className="text-gray-900">{nombre}</span>
            {payment.es_pago_parcial === true && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 ml-1" title={`${payment.dias_ocupados} días`}>
                Parcial
              </span>
            )}
          </>
        );
      }
    },
    {
      key: 'fecha_limite',
      label: 'Vencimiento',
      sortable: true,
      render: (payment) => formatLocalDate(payment.fecha_limite)
    },
    {
      key: 'monto',
      label: 'Monto',
      sortable: true,
      render: (payment) => `${payment.moneda} ${Number(payment.monto).toLocaleString()}`
    },
    {
      key: 'fecha_pago',
      label: 'Fecha Pago',
      render: (payment) => payment.fecha_pago ? formatLocalDate(payment.fecha_pago) : '-'
    },
    {
      key: 'estado',
      label: 'Estado',
      sortable: true,
      render: (payment) => {
        const badge = getPaymentStatusBadge(payment);
        return badge ? (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-medium ${badge.className}`}>
            {badge.label}
          </span>
        ) : null;
      }
    }
  ];

  const handlePaymentClick = (payment: Pago) => {
    navigate(`/payments/${payment.id}`);
  };

  const handleSendToLegal = async () => {
    if (!legalForm.notas.trim()) {
      showAlert({
        type: 'warning',
        title: 'Campo requerido',
        message: 'Las notas son requeridas',
      });
      return;
    }

    try {
      setSubmitting(true);
      await contractService.sendToLegal(Number(id), legalForm);
      showAlert({
        type: 'success',
        title: 'Éxito',
        message: 'Contrato enviado a jurídica exitosamente',
      });
      setShowLegalModal(false);
      setLegalForm({ fecha_ingreso: '', notas: '' });
      // Reload contract
      const c = await contractService.getContractById(Number(id));
      setContract((c as any).data || c);
    } catch (error: any) {
      showAlert({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.message || 'Error al enviar a jurídica',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenCancelModal = async () => {
    try {
      const balances = await contractService.getCancellationBalances(Number(id));
      setCancellationBalances((balances as any).data || balances);
      setShowCancelModal(true);
    } catch (error: any) {
      showAlert({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.message || 'Error al obtener balances',
      });
    }
  };

  const handleCancelContract = async () => {
    if (!cancelForm.fecha_cancelacion) {
      showAlert({
        type: 'warning',
        title: 'Campo requerido',
        message: 'La fecha de cancelación es requerida',
      });
      return;
    }

    const confirmed = await showConfirm({
      title: 'Cancelar Contrato',
      message: '¿Está seguro de que desea cancelar este contrato? Esta acción no se puede deshacer.',
      type: 'danger',
      confirmText: 'Sí, cancelar',
    });
    if (!confirmed) return;

    try {
      setSubmitting(true);
      await contractService.cancelContract(Number(id), cancelForm);
      showAlert({
        type: 'success',
        title: 'Éxito',
        message: 'Contrato cancelado exitosamente',
      });
      setShowCancelModal(false);
      setCancelForm({ fecha_cancelacion: '' });
      setCancellationBalances(null);
      // Reload contract
      const c = await contractService.getContractById(Number(id));
      setContract((c as any).data || c);
    } catch (error: any) {
      showAlert({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.message || 'Error al cancelar contrato',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!contract) {
    return (
      <MainLayout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📄</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Contrato no encontrado</h3>
            <Button onClick={() => navigate(-1)}>Volver</Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{contract.numero_contrato}</h1>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(contract.estado)}`}>
                {contract.estado}
              </span>
            </div>
            <p className="text-gray-600">
              Contrato automático{contract.auto_renovacion ? ' con renovación automática' : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Volver
            </Button>
            {hasPermission('contracts', 'write') && (
              <Button onClick={() => navigate(`/contracts/${id}/edit`)}>
                Editar Contrato
              </Button>
            )}
            {hasPermission('contracts', 'write') && contract.estado === EstadoContrato.ACTIVO && (
              <Button
                variant="secondary"
                onClick={() => setShowUpdateDiaPagoModal(true)}
              >
                Actualizar Día de Pago
              </Button>
            )}
            {hasPermission('contracts', 'write') && contract.estado === EstadoContrato.ACTIVO && !isInLegalList && (
              <Button
                variant="secondary"
                onClick={() => setShowLegalModal(true)}
              >
                Enviar a Jurídica
              </Button>
            )}
            {hasPermission('contracts', 'write') && contract.estado === EstadoContrato.ACTIVO && (
              <Button
                variant="secondary"
                onClick={() => setShowTransferModal(true)}
              >
                Traspaso
              </Button>
            )}
            {hasPermission('contracts', 'write') && contract.estado === EstadoContrato.ACTIVO && (
              <Button
                variant="secondary"
                onClick={handleOpenCancelModal}
              >
                Cancelar Contrato
              </Button>
            )}
          </div>
        </div>

        {/* Legal Status Badge */}
        {isInLegalList && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="text-2xl">⚖️</div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-900">En Lista Jurídica</h3>
                {contract.fecha_ingreso_lista_juridica && (
                  <p className="text-sm text-yellow-700">
                    Ingresado: {formatLocalDate(contract.fecha_ingreso_lista_juridica)}
                  </p>
                )}
                {contract.notas_lista_juridica && (
                  <p className="text-sm text-yellow-700 mt-1">{contract.notas_lista_juridica}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payment Agreement Badge (Meeting 10-02-26) */}
        {isInPaymentAgreement && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="text-2xl">📝</div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900">En Convenio de Pago</h3>
                {contract.fecha_convenio && (
                  <p className="text-sm text-blue-700">
                    Fecha del convenio: {formatLocalDate(contract.fecha_convenio)}
                  </p>
                )}
                <p className="text-xs text-blue-600 mt-1">
                  Este contrato no aparece en el reporte de pagos atrasados
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Contract Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Información del Contrato</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Dates */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Período</h3>
              {(contract.periodos && contract.periodos.length > 0 ? contract.periodos : [{
                fecha_inicio: contract.fecha_inicio,
                fecha_fin: contract.fecha_fin,
                orden: 1,
                es_prorroga: false,
              }]).map((periodo, index) => (
                <div key={`${periodo.orden}-${index}`} className="mb-2">
                  <p className="text-gray-900">
                    {formatLocalDate(periodo.fecha_inicio)} hasta {formatLocalDate(periodo.fecha_fin)}
                  </p>
                  <p className="text-gray-500 text-sm">{periodo.es_prorroga ? `Prórroga ${index}` : 'Período inicial'}</p>
                </div>
              ))}
            </div>

            {/* Payment Details */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Alquiler Inquilino</h3>
              <p className="text-2xl font-bold text-gray-900">
                {contract.moneda_inquilino} {Number(contract.monto_alquiler_inquilino).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500 mt-1">Día de pago: {contract.dia_pago}</p>
              <p className="text-sm mt-2">Agente de retención IRPF: {contract.inquilino_agente_retencion_irpf ? 'Inquilino' : 'No retiene el inquilino'}</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Alquiler Propietario</h3>
              <p className="text-2xl font-bold text-gray-900">
                {contract.moneda_propietario} {Number(currentCalculation?.net_amount ?? contract.monto_alquiler_propietario).toLocaleString()}
              </p>
              <ul className="text-sm text-gray-500 mt-1 list-disc pl-5 space-y-1">
                <li>
                  Comisión ({contract.comision}%): {contract.moneda_inquilino} {tenantCommissionAmount.toLocaleString('es-UY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </li>
                <li>
                  IVA sobre comisión ({contract.iva ?? contract.default_iva ?? 0}%): {contract.moneda_inquilino} {tenantCommissionIvaAmount.toLocaleString('es-UY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </li>
                {ownerIrpfAmount > 0 && (
                  <li>
                    {contract.inquilino_agente_retencion_irpf ? 'IRPF retenido por inquilino' : 'IRPF / Retención (aplicable)'}: {contract.moneda_inquilino} {ownerIrpfAmount.toLocaleString('es-UY', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </li>
                )}
              </ul>
            </div>

            {/* Payment Type Fields */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Forma de Pago</h3>
              <p className="text-gray-900">{getFormaPagoLabel(contract.forma_pago)}</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Tipo de Reajuste</h3>
              <p className="text-gray-900">{getTipoReajusteLabel(contract.tipo_reajuste)}</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Tipo de Alquiler</h3>
              <p className="text-gray-900">{getTipoAlquilerLabel(contract.tipo_alquiler)}</p>
            </div>

            {contract.fecha_reajuste && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Próximo Reajuste</h3>
                <p className="text-gray-900">
                  {formatLocalDate(contract.fecha_reajuste)}
                </p>
              </div>
            )}

            {(contract.garantia_nombre || contract.garantia) && (
              <div className="lg:col-span-2">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Garantía</h3>
                <p className="text-gray-900">{contract.garantia_nombre || contract.garantia}</p>
                {contract.garante_nombre && (
                  <p className="text-sm text-gray-700 mt-1">Garante: {contract.garante_nombre}</p>
                )}
                {contract.garantia_detalle && (
                  <p className="text-sm text-gray-500 mt-1">{contract.garantia_detalle}</p>
                )}
                {contract.garantia_descripcion && (
                  <p className="text-sm text-gray-500 mt-1">{contract.garantia_descripcion}</p>
                )}
              </div>
            )}
          </div>

          {contract.notas && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowNotes((prev) => !prev)}
                className="flex w-full items-center justify-between text-left"
                aria-expanded={showNotes}
              >
                <h3 className="text-sm font-medium text-gray-500">Notas</h3>
                <span className="text-sm font-medium text-blue-600">
                  {showNotes ? 'Ocultar' : 'Ver'}
                </span>
              </button>

              {showNotes && (
                <p className="mt-3 text-gray-700 whitespace-pre-wrap">{contract.notas}</p>
              )}
            </div>
          )}
        </div>

        {/* Partes del Contrato Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Partes del Contrato</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Inmueble & Propietarios Card */}
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border-2 border-blue-200">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center text-2xl">
                      {contract.inmueble?.destino_inmueble === DestinoInmueble.PARTICULAR ? '🏠' : '🏢'}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Inmueble</h3>
                      {contract.inmueble && (
                        <>
                          <p className="text-gray-900 font-medium">{contract.inmueble.direccion}</p>
                          <p className="text-sm text-gray-600">
                            {contract.inmueble.destino_inmueble === DestinoInmueble.PARTICULAR ? 'Casa/Habitacion' : 'Comercial'}
                            {contract.inmueble.ciudad && ` • ${contract.inmueble.ciudad}`}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  {contract.inmueble && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/properties/${contract.inmueble_id}`)}
                    >
                      Ver Detalles
                    </Button>
                  )}
                </div>

                {/* Propietarios */}
                {contract.propietarios && contract.propietarios.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Propietarios ({contract.propietarios.length})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {contract.propietarios.map((propietario: any) => (
                        <div
                          key={propietario.id}
                          onClick={() => navigate(`/clients/${propietario.id}`)}
                          className="bg-white rounded-lg p-4 border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
                        >
                          <div className="flex items-start gap-3 mb-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-lg">
                              👤
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-900 font-medium truncate">
                                {getClientFullName(propietario)}
                              </p>
                              <p className="text-xs text-gray-500">
                                {propietario.tipo_documento} {propietario.documento}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-2 text-sm">
                            {propietario.porcentaje_propiedad !== undefined && (
                              <div className="flex items-center justify-between bg-blue-50 rounded px-3 py-2">
                                <span className="text-gray-700 font-medium">Propiedad:</span>
                                <span className="text-blue-700 font-bold">{propietario.porcentaje_propiedad}%</span>
                              </div>
                            )}

                            <div className="flex flex-wrap gap-2">
                              {propietario.no_comision ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  ✓ Sin comisión
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                  Con comisión
                                </span>
                              )}

                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {alwaysRetainsIrpf(propietario) ? 'Siempre Retener IRPF' : 'IRPF automático según mínimo'}
                              </span>
                              <p className="w-full text-sm text-gray-700">
                                {propietario.irpf_error || (contract.inquilino_agente_retencion_irpf
                                  ? 'IRPF retenido por el inquilino'
                                  : `Retención mensual: ${contract.moneda_inquilino} ${Number(propietario.irpf_mensual ?? 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Inquilino Card */}
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-5 border-2 border-purple-200">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center text-2xl">
                      👤
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">Inquilino</h3>
                      {contract.inquilino ? (
                        <div>
                          <p className="text-gray-900 font-medium">
                            {getClientFullName(contract.inquilino)}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {contract.inquilino.tipo_documento} {contract.inquilino.documento}
                          </p>
                          <div className="flex flex-wrap gap-3 mt-2">
                            {contract.inquilino.telefono && (
                              <span className="text-sm text-gray-600">
                                📞 {contract.inquilino.telefono}
                              </span>
                            )}
                            {contract.inquilino.email && (
                              <span className="text-sm text-gray-600">
                                ✉️ {contract.inquilino.email}
                              </span>
                            )}
                          </div>
                          {contract.inquilinos_auxiliares && (
                            <div className="mt-2 pt-2 border-t border-purple-200">
                              <p className="text-xs text-gray-500 font-medium mb-1">Inquilinos Auxiliares:</p>
                              <p className="text-sm text-gray-700">{contract.inquilinos_auxiliares}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-900">ID: {contract.inquilino_id}</p>
                      )}
                    </div>
                  </div>
                  {contract.inquilino && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/clients/${contract.inquilino_id}`)}
                    >
                      Ver Detalles
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payments Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Pagos del Contrato</h2>
            <div className="text-sm text-gray-500">
              Total: {payments.length} pagos
            </div>
          </div>

          <DataTable
            columns={paymentColumns}
            data={payments}
            loading={false}
            searchable={true}
            searchPlaceholder="Buscar pagos..."
            searchFields={['tipo', 'estado', 'monto']}
            onRowClick={handlePaymentClick}
            rowKey="id"
            emptyState={
              <div className="text-center py-12">
                <div className="text-6xl mb-4">💰</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No hay pagos registrados</h3>
                <p className="text-gray-600">Los pagos se generan automáticamente al crear el contrato</p>
              </div>
            }
          />
        </div>

        {/* Adjustment History Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span>📈</span>
              Historial de Reajustes
            </h2>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/adjustments')}
            >
              Ver Factores
            </Button>
          </div>

          {loadingAdjustments ? (
            <div className="animate-pulse space-y-3">
              <div className="h-12 bg-gray-100 rounded"></div>
              <div className="h-12 bg-gray-100 rounded"></div>
            </div>
          ) : adjustmentHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">📊</div>
              <p className="text-sm">No hay reajustes aplicados a este contrato</p>
              <p className="text-xs text-gray-400 mt-1">
                Los reajustes se aplican automáticamente cuando corresponde según el aniversario del contrato
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  Este contrato ha recibido <strong>{adjustmentHistory.length}</strong> factor(es) de reajuste
                </p>
              </div>

              {adjustmentHistory.map((factorGroup: any, index: number) => {
                const isExpanded = expandedFactors.has(index);
                return (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Factor Summary Header - Clickable */}
                    <div 
                      className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200 cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-colors"
                      onClick={() => toggleFactorExpansion(index)}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-gray-600 text-lg">
                            {isExpanded ? '▼' : '▶'}
                          </span>
                          <span className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold bg-blue-600 text-white">
                            {factorGroup.anio}/{factorGroup.mes}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            +{(Number(factorGroup.factor || 0) * 100).toFixed(2)}%
                          </span>
                          <span className="text-sm text-gray-600">
                            {factorGroup.total_pagos} pago(s)
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">
                            {factorGroup.fecha_aplicacion
                              ? formatLocalDate(factorGroup.fecha_aplicacion, {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })
                              : '-'}
                          </div>
                          <div className="text-xs text-gray-500">
                            por {factorGroup.aplicado_por_username || 'Sistema'}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 flex gap-4 text-xs flex-wrap">
                        <span className="text-purple-700">
                          <strong>{factorGroup.pagos_inquilino}</strong> Inquilino
                        </span>
                        <span className="text-orange-700">
                          <strong>{factorGroup.pagos_propietario}</strong> Propietario
                        </span>
                        <span className="text-amber-700">
                          <strong>{factorGroup.pagos_creados}</strong> creados
                        </span>
                        <span className="text-blue-700">
                          <strong>{factorGroup.pagos_actualizados}</strong> actualizados
                        </span>
                      </div>
                    </div>

                    {/* Payment Details Table - Collapsible */}
                    {isExpanded && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Fecha Límite
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Tipo
                              </th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                Monto Anterior
                              </th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                Monto Nuevo
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {factorGroup.detalles?.map((detalle: any, detIdx: number) => {
                              // Calculate previous amount if payment was created (monto_anterior is null)
                              const previousAmount = detalle.monto_anterior !== null
                                ? detalle.monto_anterior
                                : calculatePreviousAmount(Number(detalle.monto_nuevo || 0), Number(factorGroup.factor || 0));
                              
                              return (
                                <tr key={detIdx} className="hover:bg-gray-50">
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                    {detalle.fecha_limite
                                      ? formatLocalDate(detalle.fecha_limite, {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric'
                                        })
                                      : '-'}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      detalle.tipo_pago === 'INQUILINO' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                                    }`}>
                                      {detalle.tipo_pago}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-500">
                                    {detalle.moneda || 'UYU'} {Number(previousAmount).toLocaleString('es-UY', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    })}
                                    {detalle.monto_anterior === null && (
                                      <span className="ml-1 text-xs text-amber-600 italic">(calculado)</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                    {detalle.moneda || 'UYU'} {Number(detalle.monto_nuevo || 0).toLocaleString('es-UY', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    })}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Send to Legal Modal */}
        <Modal
          isOpen={showLegalModal}
          onClose={() => setShowLegalModal(false)}
          title="Enviar a Lista Jurídica"
          size="md"
        >
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                Este contrato será marcado como en lista jurídica. Las notas son obligatorias.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Ingreso
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={legalForm.fecha_ingreso}
                onChange={(e) => setLegalForm({ ...legalForm, fecha_ingreso: e.target.value })}
                placeholder={toLocalDateKey(new Date())}
              />
              <p className="text-xs text-gray-500 mt-1">Si no se especifica, se usará la fecha actual</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                value={legalForm.notas}
                onChange={(e) => setLegalForm({ ...legalForm, notas: e.target.value })}
                placeholder="Motivo y detalles del envío a lista jurídica..."
                required
              />
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowLegalModal(false)}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSendToLegal}
                disabled={submitting || !legalForm.notas.trim()}
              >
                {submitting ? 'Procesando...' : 'Enviar a Jurídica'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Contract Cancellation Modal */}
        <Modal
          isOpen={showCancelModal}
          onClose={() => {
            setShowCancelModal(false);
            setCancellationBalances(null);
          }}
          title="Cancelar Contrato"
          size="md"
        >
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium">
                ⚠️ Esta acción no se puede deshacer
              </p>
              <p className="text-sm text-red-700 mt-1">
                El contrato será marcado como RESCINDIDO y el inmueble quedará disponible.
              </p>
            </div>

            {/* Contract Info */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Contrato:</span>{' '}
                  <span className="text-gray-900">{contract.numero_contrato}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Inmueble:</span>{' '}
                  <span className="text-gray-900">{contract.inmueble?.direccion || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Cancellation Balances */}
            {cancellationBalances && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-3">Saldos Pendientes</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-blue-700">Saldo Inquilino:</span>
                    <span className="font-bold text-blue-900">
                      {cancellationBalances.moneda_inquilino} {Number(cancellationBalances.saldo_inquilino || 0).toLocaleString('es-UY', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-700">Saldo Propietario:</span>
                    <span className="font-bold text-blue-900">
                      {cancellationBalances.moneda_propietario} {Number(cancellationBalances.saldo_propietario || 0).toLocaleString('es-UY', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Cancelación <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={cancelForm.fecha_cancelacion}
                onChange={(e) => setCancelForm({ ...cancelForm, fecha_cancelacion: e.target.value })}
                required
              />
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancellationBalances(null);
                }}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCancelContract}
                disabled={submitting || !cancelForm.fecha_cancelacion}
              >
                {submitting ? 'Procesando...' : 'Confirmar Cancelación'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Update Dia Pago Modal */}
        <UpdateDiaPagoModal
          isOpen={showUpdateDiaPagoModal}
          onClose={() => setShowUpdateDiaPagoModal(false)}
          contract={contract}
          onContractUpdated={async () => {
            // Reload contract and payments
            const c = await contractService.getContractById(Number(id));
            setContract((c as any).data || c);
            const p = await paymentService.getPaymentsByContract(Number(id));
            const paymentsData = (p as any).data || p;
            setPayments(Array.isArray(paymentsData) ? paymentsData : []);
          }}
        />

        {/* Transfer Contract Modal */}
        <TransferContractModal
          isOpen={showTransferModal}
          onClose={() => setShowTransferModal(false)}
          contract={contract}
          onContractUpdated={async () => {
            // Reload contract and payments
            const c = await contractService.getContractById(Number(id));
            setContract((c as any).data || c);
            const p = await paymentService.getPaymentsByContract(Number(id));
            const paymentsData = (p as any).data || p;
            setPayments(Array.isArray(paymentsData) ? paymentsData : []);
          }}
        />
      </div>
    </MainLayout>
  );
}
